// Web-app edge session bootstrap (issue #1100).
//
// Wires the shared, cross-client SessionManager (@korra/session) to the web-app's
// concrete plumbing: the redux-held access token, the new server refresh/logout
// endpoints, and the /edge/login redirect. The policy itself (30m idle, 12h
// absolute, 60m token) lives in the shared module so it stays identical to the
// editor; only the glue is here.

import { route } from "preact-router";
import {
  SessionManager,
  LogoutReason,
  LOGOUT_REASON_PARAM,
  mountSessionWarningBanner,
  createEdgeSessionEndpoints,
  normalizeLogoutReason,
  decodeEdgeSessionClaims,
  rejectionAppliesToCurrentSession,
  noteSignedOut,
  type StartOptions,
} from "@korra/session";
import { store } from "../redux/store";
import { setJWTUserIdToken } from "../redux/slices/globalsSlice";
import { get_AUTH_HOST, getIsEdge } from "../configs/env";
import { clearUserSession } from "./token-refresh";
import { endCurrentSession } from "../utils";
import logger from "../logger";

const SESSION_KEY = "Web-app-edgeUser";

let manager: SessionManager | null = null;

function currentToken(): string | null {
  const fromRedux = (store.getState() as any)?.globals?.jwtUserIdToken;
  if (fromRedux) return fromRedux;
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) return JSON.parse(stored)?.user?.userIdToken || null;
  } catch {
    /* ignore */
  }
  return null;
}

// Persist a refreshed token so a later reload / fetch-based request also carries
// it (matches the token-refresh interceptor's persistEdgeToken).
function persistToken(newToken: string): void {
  store.dispatch(setJWTUserIdToken(newToken));
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (!stored) return;
    const parsed = JSON.parse(stored);
    if (parsed?.user) {
      parsed.user.userIdToken = newToken;
      localStorage.setItem(SESSION_KEY, JSON.stringify(parsed));
    }
  } catch {
    /* best effort */
  }
}

// Refresh/revoke live in the shared module so the server contract is defined
// once for both clients (#1100). No fetchInit marker needed here: the web-app's
// interceptor is axios, which doesn't intercept these fetch() calls.
const endpoints = createEdgeSessionEndpoints({
  authHost: get_AUTH_HOST,
  getToken: currentToken,
});

// The session_id of the stored login, or null.
function storedSessionId(): string | null {
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    const token = parsed && parsed.user ? parsed.user.userIdToken : null;
    return decodeEdgeSessionClaims(token).sessionId;
  } catch {
    return null;
  }
}

function clearAndRedirect(reason: LogoutReason): void {
  // End the stats session first (#1733): this is a client-side route, so no
  // pagehide fires to end it, and endCurrentSession reads the session id that
  // clearUserSession nulls and the stored login getEffectiveUserId falls back to.
  // Without it every edge logout left the user's stats session open.
  endCurrentSession();
  // Remove the stored login only if it is the session that just ended. Another
  // tab may have signed in since and stored a NEW session; a stale tab whose old
  // session was revoked must not wipe it (#1100).
  const fromRedux = (store.getState() as any)?.globals?.jwtUserIdToken || null;
  const endedSid = decodeEdgeSessionClaims(fromRedux).sessionId;
  const storedSid = storedSessionId();
  if (!endedSid || !storedSid || endedSid === storedSid) {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
    // Published now, not when the login page loads and reads the URL marker:
    // until then another tab's pending sign-in would still pass. Only for the
    // session that ended here, like the removal above.
    noteSignedOut();
  }
  // Clear the redux-held token too: otherwise currentToken() keeps returning it
  // after logout, so the manager's timers would keep re-minting / eventually fire
  // a stale idle redirect against a user who may have signed in again (#1100).
  store.dispatch(setJWTUserIdToken(null));
  // And the global copy the logger authenticates with (appLogic only ever SETS
  // it), so log shipping stops using the signed-out token, which stays valid
  // server-side for up to its exp if the revoke didn't land (#1100).
  (globalThis as any).token = undefined;
  // Cancel any in-flight silent refresh and drop cached user / sasToken state,
  // matching the other logout paths (localLogout / logoutToLogin) that call this
  // after development added it — an edge sign-out must clear the same state.
  clearUserSession();
  emitWarnClear();
  route(
    `/edge/login?${LOGOUT_REASON_PARAM}=${encodeURIComponent(reason)}`,
    true,
  );
}

// The warning is surfaced as a window event so any component can render the
// consistent "about to be signed out" banner without this module importing UI.
function emitWarn(msLeft: number): void {
  window.dispatchEvent(
    new CustomEvent("korra-session-warning", { detail: { msLeft } }),
  );
}
function emitWarnClear(): void {
  window.dispatchEvent(new CustomEvent("korra-session-warning-clear"));
}

// Start (idempotent) the edge session manager. Call after a successful edge
// login with { fresh: true }, and with no options when an existing edge session
// is hydrated on load (the manager then refreshes at once to learn how long the
// session has left and to let the server judge idle). No-op off edge.
export function startEdgeSessionManager(opts?: StartOptions): void {
  if (!getIsEdge()) return;
  // Only manage tokens that actually carry a server session (a session_id claim):
  // password and OIDC edge logins. Mosaic and legacy tokens are claims-only; the
  // refresh/idle policy doesn't apply to them, and managing them would 401 on the
  // first refresh (#1100).
  if (!decodeEdgeSessionClaims(currentToken()).sessionId) return;
  mountSessionWarningBanner();
  if (!manager) {
    manager = new SessionManager({
      getToken: currentToken,
      setToken: persistToken,
      refresh: endpoints.refresh,
      revoke: endpoints.revoke,
      onLogout: (reason) => {
        logger.log({ message: "Edge session ended", reason });
        clearAndRedirect(reason);
      },
      onWarn: emitWarn,
      onWarnClear: emitWarnClear,
    });
  }
  manager.start(opts);
}

export function stopEdgeSessionManager(): void {
  manager?.stop();
}

// End the edge session in this tab only, without asking the server to revoke it:
// for a session already ended elsewhere (another tab signed out — see
// useEdgeCrossTabLogout via Utils.edgeLogout). Stops the manager and runs the
// same cleanup as every other edge logout (stats session, tokens, storage,
// redirect).
export function endEdgeSessionLocally(reason: LogoutReason): void {
  manager?.stop();
  clearAndRedirect(reason);
}

// Called by the axios interceptor when the server rejects a request with an
// X-Logout-Reason (session revoked / past absolute / idle). Terminal and not
// refreshable, so we tear down and redirect with the reason.
//
// requestToken is the bearer the rejected request was sent with. A request made
// by an older session can come back after the user has signed in again; its
// "session ended" must not tear down the new session, so it is ignored (#1100).
export function handleEdgeSessionRejection(
  reason: string,
  requestToken?: string | null,
): void {
  if (!rejectionAppliesToCurrentSession(requestToken, currentToken())) return;
  manager?.stop();
  clearAndRedirect(normalizeLogoutReason(reason));
}

// Log out on demand (e.g. a user-initiated Sign out button) with the manual
// reason, revoking the session server-side first.
export function logoutEdgeSession(): void {
  if (manager) {
    void manager.logout("manual");
    return;
  }
  void endpoints.revoke();
  clearAndRedirect("manual");
}
